import axios from 'axios'
import { API_BASE_URL } from '@/config/env'
import { useAuthStore } from '@/stores/auth-store'

// Not an Axios 401: stale account requests must not log out the current account.
export class SessionChangedError extends Error {
  constructor() {
    super('Your session changed. Reload this view before continuing.')
    this.name = 'SessionChangedError'
  }
}

type Capture = {
  accessToken: string | null
  refreshToken: string | null
  sessionVersion: number
}
let flight: { capture: Capture; promise: Promise<string> } | null = null

function isCurrent(capture: Capture): boolean {
  const auth = useAuthStore.getState().auth
  return (
    auth.sessionVersion === capture.sessionVersion &&
    auth.accessToken === capture.accessToken &&
    auth.refreshToken === capture.refreshToken
  )
}

/** Shared by HTTP and realtime; independent transport avoids interceptor recursion. */
export function refreshSession(): Promise<string> {
  const { accessToken, refreshToken, sessionVersion } =
    useAuthStore.getState().auth
  const capture = { accessToken, refreshToken, sessionVersion }
  if (flight && isCurrent(flight.capture)) return flight.promise
  const currentFlight = { capture, promise: Promise.resolve('') }
  currentFlight.promise = (async () => {
    let invalidResponse = false
    try {
      if (!refreshToken)
        throw new Error('Your session expired. Please sign in again.')
      const response = await axios.post<{
        access_token: string
        refresh_token?: string
      }>(
        `${API_BASE_URL}/auth/refresh`,
        { refresh_token: refreshToken },
        { timeout: 30_000 }
      )
      if (!isCurrent(capture)) throw new SessionChangedError()
      if (
        !response.data.access_token ||
        typeof response.data.access_token !== 'string'
      ) {
        invalidResponse = true
        throw new Error('Session renewal did not return an access token.')
      }
      useAuthStore.getState().auth.setTokens({
        accessToken: response.data.access_token,
        refreshToken: response.data.refresh_token ?? refreshToken,
        preserveSession: true,
      })
      return response.data.access_token
    } catch (error) {
      if (!isCurrent(capture)) throw new SessionChangedError()
      const status = axios.isAxiosError(error)
        ? error.response?.status
        : undefined
      if (
        !refreshToken ||
        invalidResponse ||
        status === 401 ||
        status === 403
      ) {
        useAuthStore.getState().auth.reset()
      }
      throw error
    } finally {
      if (flight === currentFlight) flight = null
    }
  })()
  flight = currentFlight
  // Also clears a synchronously rejected missing-token flight.
  void currentFlight.promise.then(
    () => {
      if (flight === currentFlight) flight = null
    },
    () => {
      if (flight === currentFlight) flight = null
    }
  )
  return currentFlight.promise
}

/** Untrusted expiry is only a scheduling hint; the server still authenticates. */
export function accessTokenExpiresAt(token: string): number | null {
  if (token.length > 16_384) return null
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(payload)) as { exp?: unknown }
    return typeof exp === 'number' && exp > 0 && Number.isFinite(exp * 1000)
      ? exp * 1000
      : null
  } catch {
    return null
  }
}
